import React, { useEffect, useMemo, useState, useRef } from 'react';
import { Table, message, Button, Space, Row, Col, Input, Select, DatePicker, Tooltip, Tag, Card, App, Typography } from 'antd';
import { useAuth0 } from '@auth0/auth0-react';
import { useParams, useNavigate } from 'react-router-dom';
import { PrinterOutlined, EditOutlined, PlusOutlined, FieldTimeOutlined, SearchOutlined, AppstoreOutlined, DatabaseOutlined, CheckCircleOutlined, WarningOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import type { SortOrder } from 'antd/es/table/interface';
import { Producto } from '../../types/common/producto';
import { PageTitle, StyledCard, SearchButton, ExportExcelButton, ExportPDFButton, ColumnManagerDropdown } from '../common';
import { useColumnManager } from '../../hooks';
import { theme } from '../../theme';
import dayjs, { Dayjs } from 'dayjs';
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import 'jspdf-autotable';

const { Option } = Select;
const { RangePicker } = DatePicker;
const { Text } = Typography;

interface Pesaje {
  id: number;
  numero_ticket: number;
  fecha_ticket: string;
  producto_nombre: string;
  producto_tipo: string;
  presentacion_nombre: string;
  ingreso_egreso: string;
  compra_servicio: string;
  cliente_nombre: string;
  productor_nombre: string;
  transportista_nombre: string;
  chofer_nombre: string;
  camion_patente: string;
  kilos_primera: number;
  kilos_segunda: number;
  dtv: string;
  numero_remito: string;
  [key: string]: any; // Índice de firma para permitir acceso dinámico
}

type FiltrosState = {
  producto?: number;
  estado?: string;
  fechaRango: [Dayjs | null, Dayjs | null];
};

const filtrosIniciales: FiltrosState = {
  producto: undefined,
  estado: undefined,
  fechaRango: [dayjs().subtract(30, 'day'), dayjs()],
};

// Función para cargar filtros desde localStorage
const cargarFiltrosDesdeStorage = (): FiltrosState => {
  try {
    const saved = localStorage.getItem('pesaje_filtros');
    if (saved) {
      const parsed = JSON.parse(saved);
      return {
        producto: parsed.producto || undefined,
        estado: parsed.estado || undefined,
        fechaRango: parsed.fechaRango 
          ? [parsed.fechaRango[0] ? dayjs(parsed.fechaRango[0]) : null, parsed.fechaRango[1] ? dayjs(parsed.fechaRango[1]) : null]
          : filtrosIniciales.fechaRango
      };
    }
  } catch (error) {
    console.error('Error al cargar filtros desde localStorage:', error);
  }
  return filtrosIniciales;
};

// Función para guardar filtros en localStorage
const guardarFiltrosEnStorage = (filtros: FiltrosState) => {
  try {
    const filtrosParaGuardar = {
      producto: filtros.producto,
      estado: filtros.estado,
      fechaRango: filtros.fechaRango.map(fecha => fecha ? fecha.format('YYYY-MM-DD') : null)
    };
    localStorage.setItem('pesaje_filtros', JSON.stringify(filtrosParaGuardar));
  } catch (error) {
    console.error('Error al guardar filtros en localStorage:', error);
  }
};

const ListadoPesajes: React.FC = () => {
  const { message: messageApi } = App.useApp();
  const containerRef = useRef<HTMLDivElement>(null);
  const [pesajes, setPesajes] = useState<Pesaje[]>([]);
  const [loading, setLoading] = useState(false);
  const [productos, setProductos] = useState<Producto[]>([]);
  const [loadingProductos, setLoadingProductos] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(100);
  const [serverStats, setServerStats] = useState({ total: 0, ingresos: 0, egresos: 0, kilosTotal: 0 });
  const { getAccessTokenSilently } = useAuth0();
  const {user} = useAuth0();
  const id_empresa = user?.id_empresa;
  const id_sucursal = user?.id_sucursal;
  
  // Cargar estados desde localStorage
  const [filteredInfo, setFilteredInfo] = useState<Record<string, any>>(() => {
    try {
      const saved = localStorage.getItem('pesaje_filteredInfo');
      return saved ? JSON.parse(saved) : {};
    } catch {
      return {};
    }
  });
  
  const [busquedaRapida, setBusquedaRapida] = useState<string>(() => {
    try {
      return localStorage.getItem('pesaje_busquedaRapida') || '';
    } catch {
      return '';
    }
  });
  
  const [sorterInfo, setSorterInfo] = useState<{ columnKey?: string; order?: SortOrder }>(() => {
    try {
      const saved = localStorage.getItem('pesaje_sorterInfo');
      return saved ? JSON.parse(saved) : {};
    } catch {
      return {};
    }
  });
  
  const navigate = useNavigate();
  const [tipoRegistro, setTipoRegistro] = useState('0');

  // Filtros de búsqueda - cargar desde localStorage
  const [filtros, setFiltros] = useState<FiltrosState>(cargarFiltrosDesdeStorage);

  // Lista de todas las columnas disponibles (keys)
  const allColumnKeys = [
    'fecha_ticket',
    'numero_ticket',
    'dtv',
    'numero_remito',
    'producto_nombre',
    'presentacion_nombre',
    'ingreso_egreso',
    'compra_servicio',
    'cliente_nombre',
    'productor_nombre',
    'transportista_nombre',
    'chofer_nombre',
    'camion_patente',
    'kilos_primera',
    'kilos_segunda',
    'acciones'
  ];

  // Nombres amigables para las columnas
  const columnNames: Record<string, string> = {
    'fecha_ticket': 'Fecha',
    'numero_ticket': 'Ticket',
    'dtv': 'DTV',
    'numero_remito': 'Remito',
    'producto_nombre': 'Producto',
    'presentacion_nombre': 'Presentación',
    'ingreso_egreso': 'Ingreso/Egreso',
    'compra_servicio': 'Motivo',
    'cliente_nombre': 'Cliente',
    'productor_nombre': 'Productor',
    'transportista_nombre': 'Transportista',
    'chofer_nombre': 'Chofer',
    'camion_patente': 'Patente',
    'kilos_primera': '1ra Pasada',
    'kilos_segunda': '2da Pasada',
    'acciones': 'Acciones'
  };

  // Visibilidad + orden de columnas (persistido) — reemplaza el manejo manual de antes.
  const columnManager = useColumnManager({
    storageKey: 'pesaje',
    allKeys: allColumnKeys,
    requiredKeys: ['acciones', 'numero_ticket'],
  });

  // Guardar busquedaRapida en localStorage cuando cambie (con debounce para evitar guardados excesivos)
  useEffect(() => {
    const timer = setTimeout(() => {
      localStorage.setItem('pesaje_busquedaRapida', busquedaRapida);
    }, 300); // Guardar después de 300ms de inactividad
    
    return () => clearTimeout(timer);
  }, [busquedaRapida]);

  // Guardar filtros en localStorage cuando cambien (con debounce)
  useEffect(() => {
    const timer = setTimeout(() => {
      guardarFiltrosEnStorage(filtros);
    }, 300); // Guardar después de 300ms de inactividad
    
    return () => clearTimeout(timer);
  }, [filtros]);


  // Cargar productos al inicio
  useEffect(() => {
    const cargarProductos = async () => {
      setLoadingProductos(true);
      try {
        const token = await getAccessTokenSilently();
        const response = await fetch(`${process.env.REACT_APP_BALANZA_URL}/productos/`, {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });

        if (!response.ok) {
          throw new Error(`Error al cargar productos: ${response.status}`);
        }

        const data = await response.json();
        console.log('Productos cargados:', data);
        console.log('Productos tipo 2:', data.filter((p: Producto) => p.tipo === 2));
        console.log('Tipos de productos (valores):', data.map((p: Producto) => ({ id: p.id, descripcion: p.descripcion, tipo: p.tipo, tipoType: typeof p.tipo })));
        setProductos(data);
      } catch (error) {
        console.error('Error al cargar productos:', error);
        messageApi.error('Error al cargar los productos');
      } finally {
        setLoadingProductos(false);
      }
    };

    cargarProductos();
  }, [getAccessTokenSilently]);

  const handleSegundaPasada = (record: Pesaje) => {
    if (record) {
      console.log(record.id);
      navigate(`/segunda_pesada/${record.id}`);
    }
  };

  const handleNuevo = () => {
    // Agregar clase de transición para slide out
    if (containerRef.current) {
      containerRef.current.classList.add('slide-out-left');
      // Navegar después de que comience la animación
      setTimeout(() => {
        navigate('/pesaje');
      }, 300); // Duración de la animación (300ms según especificaciones de Ant Design)
    } else {
      navigate('/pesaje');
    }
  };

  const handleEditar = (record: Pesaje) => {
    if (!id_empresa || !id_sucursal) {
      messageApi.warning('No se encontraron los datos de empresa/sucursal del usuario.');
      return;
    }
    navigate(`/balanza/editar/${id_empresa}/${id_sucursal}/${record.id}`);
  };

  const handleImprimir = (record: Pesaje) => {
    navigate(`/imprimir_balanza/${record.id}`);
  };

  const handleDetalleModuloRollos = (record: Pesaje) => {
    navigate(`/altarollos/${record.id}`);
  };

  // Función optimizada para filtrar datos - unifica todas las búsquedas
  const getFilteredData = useMemo(() => {
    let filtered = [...pesajes];

    // Aplicar búsqueda global si existe
    const termino = busquedaRapida.trim().toLowerCase();
    if (termino) {
      const camposBusqueda = [
        'numero_ticket',
        'producto_nombre',
        'presentacion_nombre',
        'cliente_nombre',
        'productor_nombre',
        'transportista_nombre',
        'chofer_nombre',
        'camion_patente',
        'numero_remito',
        'dtv',
        'ingreso_egreso',
        'compra_servicio',
      ];
      
      filtered = filtered.filter(item =>
        camposBusqueda.some(campo =>
          String(item[campo] ?? '')
            .toLowerCase()
            .includes(termino)
        )
      );
    }

    // Ordenar por fecha (descendente) y luego por número de ticket (descendente)
    return filtered.sort((a, b) => {
      const fechaA = new Date(a.fecha_ticket);
      const fechaB = new Date(b.fecha_ticket);
      
      if (fechaA > fechaB) return -1;
      if (fechaA < fechaB) return 1;
      
      return b.numero_ticket - a.numero_ticket;
    });
  }, [pesajes, busquedaRapida]);

  // Función para obtener productos filtrados según el estado
  const getProductosFiltrados = () => {
    if (filtros.estado === '1') {
      // Para estado "1" (Pendiente desmote), mostrar solo productos tipo 2 (numérico)
      const productosTipo2 = productos.filter(producto => producto.tipo === 2);
      console.log('Productos tipo 2 filtrados:', productosTipo2);
      console.log('Total productos:', productos.length);
      console.log('Tipos de productos disponibles:', Array.from(new Set(productos.map(p => p.tipo))));
      return productosTipo2;
    }
    // Para otros estados, mostrar todos los productos
    return productos;
  };

  // Función para manejar el cambio de estado
  const handleEstadoChange = (value: string | undefined) => {
    setFiltros(prev => ({ 
      ...prev, 
      estado: value,
      // Limpiar el producto seleccionado si cambia el estado
      producto: undefined 
    }));
  };

  const handleResetFiltros = () => {
    setFiltros(filtrosIniciales);
    setBusquedaRapida('');
    setFilteredInfo({});
    setSorterInfo({});
    // Limpiar localStorage
    localStorage.removeItem('pesaje_filtros');
    localStorage.removeItem('pesaje_busquedaRapida');
    localStorage.removeItem('pesaje_filteredInfo');
    localStorage.removeItem('pesaje_sorterInfo');
  };

  // Función para realizar la búsqueda con filtros
  const handleBuscar = async (page = 1, newPageSize = pageSize) => {
    // Validación para estado "1" (Pendiente desmote)
    if (filtros.estado === '1') {
      if (!filtros.producto) {
        messageApi.warning('Para "Pendiente Desmote" debe seleccionar un producto tipo 2');
        return;
      }
      const productoSeleccionado = productos.find(p => p.id === filtros.producto);
      if (!productoSeleccionado || productoSeleccionado.tipo !== 2) {
        messageApi.warning('Para "Pendiente Desmote" debe seleccionar un producto tipo 2');
        return;
      }
    }

    // Guardar filtros en localStorage antes de buscar
    guardarFiltrosEnStorage(filtros);
    localStorage.setItem('pesaje_busquedaRapida', busquedaRapida);
    localStorage.setItem('pesaje_filteredInfo', JSON.stringify(filteredInfo));
    localStorage.setItem('pesaje_sorterInfo', JSON.stringify(sorterInfo));

    setLoading(true);
    try {
      const token = await getAccessTokenSilently();
      const productoId = filtros.producto !== undefined ? filtros.producto : 0;
      const fechaDesde = filtros.fechaRango[0]?.format('YYYY-MM-DD') || '0';
      const fechaHasta = filtros.fechaRango[1]?.format('YYYY-MM-DD') || '0';
      const estado = filtros.estado !== undefined ? filtros.estado : '0';

      const url = `${process.env.REACT_APP_BALANZA_URL}/listar_balanza/${id_empresa}/${id_sucursal}/${productoId}/${fechaDesde}/${fechaHasta}/${estado}/?page=${page}&page_size=${newPageSize}`;

      console.log('URL de búsqueda:', url);

      const response = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!response.ok) throw new Error('Error al obtener los datos');

      const responseData = await response.json();
      if (responseData.status === 'success') {
        setPesajes(responseData.data);
        setCurrentPage(page);
        setPageSize(newPageSize);
        setServerStats({
          total: responseData.count || 0,
          ingresos: responseData.total_ingresos || 0,
          egresos: responseData.total_egresos || 0,
          kilosTotal: responseData.kilos_primera_total || 0,
        });
        messageApi.success(`Mostrando ${responseData.data.length} de ${responseData.count} registros`);
      } else {
        setPesajes([]);
        setServerStats({ total: 0, ingresos: 0, egresos: 0, kilosTotal: 0 });
        messageApi.info('No se encontraron registros con los filtros especificados');
      }
    } catch (error) {
      console.error('Error:', error);
      messageApi.error('Error al realizar la búsqueda');
    } finally {
      setLoading(false);
    }
  };

  // Función para resaltar términos en el texto
  const highlightText = (text: string, searchTerm: string) => {
    if (!searchTerm.trim() || !text) return text;
    
    const regex = new RegExp(`(${searchTerm.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi');
    const parts = text.split(regex);
    
    return parts.map((part, index) => 
      regex.test(part) ? (
        <mark key={index} style={{ 
          backgroundColor: '#fff3cd', 
          padding: '2px 4px',
          borderRadius: '3px',
          fontWeight: 600
        }}>
          {part}
        </mark>
      ) : part
    );
  };

  // Calcular estadísticas mejoradas
  const estadisticas = useMemo(() => {
    const filtered = getFilteredData;
    // Totales vienen del servidor (reflejan todos los registros, no solo la página actual)
    const total = serverStats.total || filtered.length;
    const ingresos = serverStats.ingresos || filtered.filter(p => p.ingreso_egreso === 'Ingreso').length;
    const egresos = serverStats.egresos || filtered.filter(p => p.ingreso_egreso === 'Egreso').length;
    const kilosTotal = serverStats.kilosTotal || filtered.reduce((acc, p) => acc + (Number(p.kilos_primera) || 0), 0);
    const kilosSegunda = filtered.reduce((acc, p) => acc + (Number(p.kilos_segunda) || 0), 0);

    // Top 3 productos (página actual)
    const productosCount = filtered.reduce((acc, record) => {
      const prod = record.producto_nombre || 'Sin producto';
      acc[prod] = (acc[prod] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);

    const topProductos = Object.entries(productosCount)
      .sort(([, a], [, b]) => b - a)
      .slice(0, 3)
      .map(([nombre, count]) => ({ nombre, count }));

    // Top 3 clientes (página actual)
    const clientesCount = filtered.reduce((acc, record) => {
      const cliente = record.cliente_nombre || 'Sin cliente';
      acc[cliente] = (acc[cliente] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);

    const topClientes = Object.entries(clientesCount)
      .sort(([, a], [, b]) => b - a)
      .slice(0, 3)
      .map(([nombre, count]) => ({ nombre, count }));

    return {
      total,
      ingresos,
      egresos,
      kilosTotal,
      kilosSegunda,
      topProductos,
      topClientes
    };
  }, [getFilteredData, serverStats]);

  // Función para exportar a Excel
  const handleExportExcel = () => {
    try {
      const motivos: Record<string, string> = {
        'C': 'Compra', 'S': 'Servicio', 'V': 'Venta', 'A': 'A Otra Sucursal',
        'D': 'Cambio Dueño', 'E': 'Retiro Cliente', 'B': 'Baja',
        'F': 'Desde Sucursal', 'R': 'Recupero'
      };
      const exportData = getFilteredData.map((record: any) => {
        const exportRow: Record<string, any> = {};
        columnManager.orderedVisibleKeys.forEach(key => {
          if (key === 'acciones') return;
          let value = record[key] ?? '';
          if (key === 'compra_servicio') value = motivos[value] || value;
          exportRow[columnNames[key] || key] = value;
        });
        return exportRow;
      });
      const worksheet = XLSX.utils.json_to_sheet(exportData);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Pesajes');
      XLSX.writeFile(workbook, `pesajes_${new Date().toISOString().split('T')[0]}.xlsx`);
      messageApi.success('Archivo Excel exportado correctamente');
    } catch (error) {
      console.error('Error al exportar Excel:', error);
      messageApi.error('Error al exportar el archivo Excel');
    }
  };

  // Función para exportar a PDF
  const handleExportPDF = () => {
    try {
      const filtered = getFilteredData;
      const doc = new jsPDF();
      doc.setFontSize(16);
      doc.text('Listado de Pesajes', 14, 15);
      doc.setFontSize(10);
      doc.text(`Fecha: ${new Date().toLocaleDateString('es-AR')}`, 14, 22);
      doc.text(`Total de registros: ${filtered.length}`, 14, 28);

      const tableData = filtered.map(record => [
        record.fecha_ticket,
        record.numero_ticket.toString(),
        (record.producto_nombre || '').substring(0, 15),
        record.ingreso_egreso,
        (record.kilos_primera || 0).toString()
      ]);

      (doc as any).autoTable({
        head: [['Fecha', 'Ticket', 'Producto', 'Tipo', 'Kilos']],
        body: tableData,
        startY: 35,
        styles: { fontSize: 8 },
        headStyles: { fillColor: [45, 92, 146] }
      });

      doc.save(`pesajes_${new Date().toISOString().split('T')[0]}.pdf`);
      messageApi.success('Archivo PDF exportado correctamente');
    } catch (error) {
      console.error('Error al exportar PDF:', error);
      messageApi.error('Error al exportar el archivo PDF');
    }
  };

  const columns: ColumnsType<Pesaje> = [
    {
      title: <span style={{ color: theme.colors.textPrimary, fontWeight: 600, fontSize: '13px', whiteSpace: 'nowrap' }}>Fecha</span>,
      dataIndex: 'fecha_ticket',
      key: 'fecha_ticket',
      width: 110,
      sorter: (a, b) => dayjs(a.fecha_ticket).unix() - dayjs(b.fecha_ticket).unix(),
      sortOrder: sorterInfo.columnKey === 'fecha_ticket' ? sorterInfo.order : undefined,
      render: (value: string) => (
        <span style={{ color: theme.colors.textPrimary, fontWeight: 500 }}>
          {dayjs(value).format('DD/MM/YYYY')}
        </span>
      ),
    },
    {
      title: <span style={{ color: theme.colors.textPrimary, fontWeight: 600, fontSize: '13px', whiteSpace: 'nowrap' }}>Ticket</span>,
      dataIndex: 'numero_ticket',
      key: 'numero_ticket',
      width: 90,
      align: 'right' as const,
      sorter: (a, b) => a.numero_ticket - b.numero_ticket,
      sortOrder: sorterInfo.columnKey === 'numero_ticket' ? sorterInfo.order : undefined,
      render: (value: number) => (
        <span style={{ color: theme.colors.textPrimary, fontWeight: 600 }}>{value}</span>
      ),
    },
    {
      title: <span style={{ color: theme.colors.textPrimary, fontWeight: 600, fontSize: '13px', whiteSpace: 'nowrap' }}>DTV</span>,
      dataIndex: 'dtv',
      key: 'dtv',
      width: 100,
      render: (text: string) => (
        <span style={{ color: theme.colors.textSecondary }}>{text || '-'}</span>
      ),
    },
    {
      title: <span style={{ color: theme.colors.textPrimary, fontWeight: 600, fontSize: '13px', whiteSpace: 'nowrap' }}>Remito</span>,
      dataIndex: 'numero_remito',
      key: 'numero_remito',
      width: 120,
      render: (text: string) => (
        <span style={{ color: theme.colors.textSecondary }}>{text || '-'}</span>
      ),
    },
    {
      title: <span style={{ color: theme.colors.textPrimary, fontWeight: 600, fontSize: '13px', whiteSpace: 'nowrap' }}>Producto</span>,
      dataIndex: 'producto_nombre',
      key: 'producto_nombre',
      width: 150,
      ellipsis: true,
      filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
        <div style={{ padding: 8 }}>
          <Input
            placeholder="Buscar Producto"
            value={selectedKeys[0] as string || ''}
            onChange={e => setSelectedKeys(e.target.value ? [e.target.value] : [])}
            style={{ width: 188, marginBottom: 8, display: 'block' }}
            onPressEnter={() => confirm()}
          />
          <Space>
            <Button type="primary" size="small" onClick={() => confirm()} style={{ width: 90 }}>
              Buscar
            </Button>
            <Button size="small" onClick={() => { clearFilters?.(); confirm(); }} style={{ width: 90 }}>
              Limpiar
            </Button>
          </Space>
        </div>
      ),
      filterIcon: (filtered: boolean) => (
        <SearchOutlined style={{ color: filtered ? '#1890ff' : undefined }} />
      ),
      render: (text: string) => (
        <span style={{ color: theme.colors.textPrimary, fontWeight: 500 }}>{text}</span>
      ),
    },
    {
      title: <span style={{ color: theme.colors.textPrimary, fontWeight: 600, fontSize: '13px', whiteSpace: 'nowrap' }}>Presentación</span>,
      dataIndex: 'presentacion_nombre',
      key: 'presentacion_nombre',
      width: 150,
      ellipsis: true,
      filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
        <div style={{ padding: 8 }}>
          <Input
            placeholder="Buscar Presentación"
            value={selectedKeys[0] as string || ''}
            onChange={e => setSelectedKeys(e.target.value ? [e.target.value] : [])}
            style={{ width: 188, marginBottom: 8, display: 'block' }}
            onPressEnter={() => confirm()}
          />
          <Space>
            <Button type="primary" size="small" onClick={() => confirm()} style={{ width: 90 }}>
              Buscar
            </Button>
            <Button size="small" onClick={() => { clearFilters?.(); confirm(); }} style={{ width: 90 }}>
              Limpiar
            </Button>
          </Space>
        </div>
      ),
      filterIcon: (filtered: boolean) => (
        <SearchOutlined style={{ color: filtered ? '#1890ff' : undefined }} />
      ),
      render: (text: string) => (
        <span style={{ color: theme.colors.textSecondary }}>{text || '-'}</span>
      ),
    },
    {
      title: <span style={{ color: theme.colors.textPrimary, fontWeight: 600, fontSize: '13px', whiteSpace: 'nowrap' }}>Ingreso/Egreso</span>,
      dataIndex: 'ingreso_egreso',
      key: 'ingreso_egreso',
      width: 140,
      align: 'center' as const,
      filters: [
        { text: 'Ingreso', value: 'I' },
        { text: 'Egreso', value: 'E' },
      ],
      filteredValue: filteredInfo.ingreso_egreso || null,
      onFilter: (value: any, record: Pesaje) => record.ingreso_egreso === value,
      render: (text: string) => (
        <span style={{ 
          display: 'inline-block',
          padding: '4px 12px',
          borderRadius: '12px',
          fontSize: '12px',
          fontWeight: 600,
          backgroundColor: text === 'I' ? '#d4edda' : '#f8d7da',
          color: text === 'I' ? '#155724' : '#721c24'
        }}>
          {text === 'I' ? 'Ingreso' : 'Egreso'}
        </span>
      ),
    },
    {
      title: <span style={{ color: theme.colors.textPrimary, fontWeight: 600, fontSize: '13px', whiteSpace: 'nowrap' }}>Motivo</span>,
      dataIndex: 'compra_servicio',
      key: 'compra_servicio',
      width: 150,
      align: 'center' as const,
      filters: [
        { text: 'Compra', value: 'C' },
        { text: 'Servicio', value: 'S' },
        { text: 'Venta', value: 'V' },
        { text: 'A Otra Sucursal', value: 'A' },
        { text: 'Cambio Dueño', value: 'D' },
        { text: 'Retiro Cliente', value: 'E' },
        { text: 'Baja', value: 'B' },
        { text: 'Desde Sucursal', value: 'F' },
        { text: 'Recupero', value: 'R' },
      ],
      filteredValue: filteredInfo.compra_servicio || null,
      onFilter: (value: any, record: Pesaje) => record.compra_servicio === value,
      render: (text: string) => {
        const motivos: { [key: string]: string } = {
          'C': 'Compra',
          'S': 'Servicio',
          'V': 'Venta',
          'A': 'A Otra Sucursal',
          'D': 'Cambio Dueño',
          'E': 'Retiro Cliente',
          'B': 'Baja',
          'F': 'Desde Sucursal',
          'R': 'Recupero'
        };
        return (
          <span style={{ color: theme.colors.textSecondary, fontSize: '12px' }}>
            {motivos[text] || text}
          </span>
        );
      },
    },
    {
      title: <span style={{ color: theme.colors.textPrimary, fontWeight: 600, fontSize: '13px', whiteSpace: 'nowrap' }}>Cliente</span>,
      dataIndex: 'cliente_nombre',
      key: 'cliente_nombre',
      width: 200,
      ellipsis: true,
      filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }: any) => (
        <div style={{ padding: 8 }}>
          <Input
            placeholder="Buscar Cliente"
            value={selectedKeys[0] as string || ''}
            onChange={e => setSelectedKeys(e.target.value ? [e.target.value] : [])}
            style={{ width: 188, marginBottom: 8, display: 'block' }}
            onPressEnter={() => confirm()}
          />
          <Space>
            <Button type="primary" size="small" onClick={() => confirm()} style={{ width: 90 }}>
              Buscar
            </Button>
            <Button size="small" onClick={() => { clearFilters?.(); confirm(); }} style={{ width: 90 }}>
              Limpiar
            </Button>
          </Space>
        </div>
      ),
      filterIcon: (filtered: boolean) => (
        <SearchOutlined style={{ color: filtered ? '#1890ff' : undefined }} />
      ),
      render: (text: string) => (
        <span style={{ color: theme.colors.textPrimary, fontWeight: 500 }}>{text || '-'}</span>
      ),
    },
    {
      title: <span style={{ color: theme.colors.textPrimary, fontWeight: 600, fontSize: '13px', whiteSpace: 'nowrap' }}>Productor</span>,
      dataIndex: 'productor_nombre',
      key: 'productor_nombre',
      width: 200,
      ellipsis: true,
      filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }: any) => (
        <div style={{ padding: 8 }}>
          <Input
            placeholder="Buscar Productor"
            value={selectedKeys[0] as string || ''}
            onChange={e => setSelectedKeys(e.target.value ? [e.target.value] : [])}
            style={{ width: 188, marginBottom: 8, display: 'block' }}
            onPressEnter={() => confirm()}
          />
          <Space>
            <Button type="primary" size="small" onClick={() => confirm()} style={{ width: 90 }}>
              Buscar
            </Button>
            <Button size="small" onClick={() => { clearFilters?.(); confirm(); }} style={{ width: 90 }}>
              Limpiar
            </Button>
          </Space>
        </div>
      ),
      filterIcon: (filtered: boolean) => (
        <SearchOutlined style={{ color: filtered ? '#1890ff' : undefined }} />
      ),
      render: (text: string) => (
        <span style={{ color: theme.colors.textSecondary }}>{text || '-'}</span>
      ),
    },
    {
      title: <span style={{ color: theme.colors.textPrimary, fontWeight: 600, fontSize: '13px', whiteSpace: 'nowrap' }}>Transportista</span>,
      dataIndex: 'transportista_nombre',
      key: 'transportista_nombre',
      width: 200,
      ellipsis: true,
      filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }: any) => (
        <div style={{ padding: 8 }}>
          <Input
            placeholder="Buscar Transportista"
            value={selectedKeys[0] as string || ''}
            onChange={e => setSelectedKeys(e.target.value ? [e.target.value] : [])}
            style={{ width: 188, marginBottom: 8, display: 'block' }}
            onPressEnter={() => confirm()}
          />
          <Space>
            <Button type="primary" size="small" onClick={() => confirm()} style={{ width: 90 }}>
              Buscar
            </Button>
            <Button size="small" onClick={() => { clearFilters?.(); confirm(); }} style={{ width: 90 }}>
              Limpiar
            </Button>
          </Space>
        </div>
      ),
      filterIcon: (filtered: boolean) => (
        <SearchOutlined style={{ color: filtered ? '#1890ff' : undefined }} />
      ),
      render: (text: string) => (
        <span style={{ color: theme.colors.textSecondary }}>{text || '-'}</span>
      ),
    },
    {
      title: <span style={{ color: theme.colors.textPrimary, fontWeight: 600, fontSize: '13px', whiteSpace: 'nowrap' }}>Chofer</span>,
      dataIndex: 'chofer_nombre',
      key: 'chofer_nombre',
      width: 150,
      ellipsis: true,
      filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }: any) => (
        <div style={{ padding: 8 }}>
          <Input
            placeholder="Buscar Chofer"
            value={selectedKeys[0] as string || ''}
            onChange={e => setSelectedKeys(e.target.value ? [e.target.value] : [])}
            style={{ width: 188, marginBottom: 8, display: 'block' }}
            onPressEnter={() => confirm()}
          />
          <Space>
            <Button type="primary" size="small" onClick={() => confirm()} style={{ width: 90 }}>
              Buscar
            </Button>
            <Button size="small" onClick={() => { clearFilters?.(); confirm(); }} style={{ width: 90 }}>
              Limpiar
            </Button>
          </Space>
        </div>
      ),
      filterIcon: (filtered: boolean) => (
        <SearchOutlined style={{ color: filtered ? '#1890ff' : undefined }} />
      ),
      render: (text: string) => (
        <span style={{ color: theme.colors.textSecondary }}>{text || '-'}</span>
      ),
    },
    {
      title: <span style={{ color: theme.colors.textPrimary, fontWeight: 600, fontSize: '13px', whiteSpace: 'nowrap' }}>Patente</span>,
      dataIndex: 'camion_patente',
      key: 'camion_patente',
      width: 100,
      render: (text: string) => (
        <span style={{ color: theme.colors.textSecondary }}>{text || '-'}</span>
      ),
    },
    {
      title: <span style={{ color: theme.colors.textPrimary, fontWeight: 600, fontSize: '13px', whiteSpace: 'nowrap' }}>1ra Pasada</span>,
      dataIndex: 'kilos_primera',
      key: 'kilos_primera',
      width: 120,
      align: 'right' as const,
      sorter: (a, b) => a.kilos_primera - b.kilos_primera,
      sortOrder: sorterInfo.columnKey === 'kilos_primera' ? sorterInfo.order : undefined,
      render: (value: number) => (
        <span style={{ color: theme.colors.textPrimary, fontWeight: 600 }}>
          {value ? value.toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '-'}
        </span>
      ),
    },
    {
      title: <span style={{ color: theme.colors.textPrimary, fontWeight: 600, fontSize: '13px', whiteSpace: 'nowrap' }}>2da Pasada</span>,
      dataIndex: 'kilos_segunda',
      key: 'kilos_segunda',
      width: 120,
      align: 'right' as const,
      sorter: (a, b) => a.kilos_segunda - b.kilos_segunda,
      sortOrder: sorterInfo.columnKey === 'kilos_segunda' ? sorterInfo.order : undefined,
      render: (value: number) => (
        <span style={{ color: theme.colors.textPrimary, fontWeight: 600 }}>
          {value ? value.toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '-'}
        </span>
      ),
    },
    {
      title: <span style={{ color: theme.colors.textPrimary, fontWeight: 600, fontSize: '13px', whiteSpace: 'nowrap' }}>Acciones</span>,
      key: 'acciones',
      width: 200,
      fixed: 'right' as const,
      render: (_, record) => (
        <Space size="small">
          <Tooltip title={record.kilos_segunda > 0 ? "Ya tiene 2da pasada" : "Registrar 2da pasada"}>
            <Button
              icon={<FieldTimeOutlined />}
              size="small"
              onClick={() => handleSegundaPasada(record)}
              disabled={record.kilos_segunda > 0}
              style={{
                backgroundColor: record.kilos_segunda === 0 ? theme.colors.warning : undefined,
                borderColor: record.kilos_segunda === 0 ? theme.colors.warning : undefined,
                color: record.kilos_segunda === 0 ? 'white' : undefined
              }}
            />
          </Tooltip>
          <Tooltip title="Editar">
            <Button
              type="primary"
              icon={<EditOutlined />}
              size="small"
              onClick={() => handleEditar(record)}
            />
          </Tooltip>
          <Tooltip title="Imprimir ticket">
            <Button
              icon={<PrinterOutlined />}
              size="small"
              onClick={() => handleImprimir(record)}
            />
          </Tooltip>
          {record.producto_tipo === "1" && (
            <Tooltip title="Detalle Módulos/Rollos">
              <Button
                icon={<AppstoreOutlined />}
                size="small"
                onClick={() => handleDetalleModuloRollos(record)}
                style={{
                  backgroundColor: theme.colors.secondary,
                  borderColor: theme.colors.secondary,
                  color: 'white'
                }}
              />
            </Tooltip>
          )}
        </Space>
      ),
    },
  ];

  // Columnas visibles, en el orden elegido por el usuario en el selector de columnas
  const filteredColumns = useMemo(() => {
    const columnsByKey = new Map(columns.map(col => [col.key as string, col]));
    return columnManager.orderedVisibleKeys
      .map(key => columnsByKey.get(key))
      .filter((col): col is (typeof columns)[number] => Boolean(col));
  }, [columnManager.orderedVisibleKeys, columns]);

  const fetchData = async (tipo?: string) => {
    setLoading(true);
    try {
      const token = await getAccessTokenSilently();
      const url = tipo !== undefined 
        ? `${process.env.REACT_APP_BALANZA_URL}/listar_pesaje/${id_empresa}/${id_sucursal}/${tipo}/0`
        : `${process.env.REACT_APP_BALANZA_URL}/listar_pesaje/${id_empresa}/${id_sucursal}/0/0`;
      
      const response = await fetch(url, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error('Error al obtener los datos');
      }

      const responseData = await response.json();
      if (responseData.status === 'success') {
        setPesajes(responseData.data);
      }
    } catch (error) {
      console.error('Error:', error);
      messageApi.error('Error al cargar los datos');
    } finally {
      setLoading(false);
    }
  };





  return (
    <App>
      <div ref={containerRef} className="page-transition-container" style={{ padding: '0', width: '100%', maxWidth: '100vw', overflowX: 'hidden' }}>
        <style>{`
          .page-transition-container {
            transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.3s ease;
            transform: translateX(0);
            opacity: 1;
          }
          .page-transition-container.slide-out-left {
            transform: translateX(-100%);
            opacity: 0;
          }
          .page-transition-container.slide-in-right {
            animation: slideInRight 0.3s cubic-bezier(0.4, 0, 0.2, 1);
          }
          @keyframes slideInRight {
            from {
              transform: translateX(100%);
              opacity: 0;
            }
            to {
              transform: translateX(0);
              opacity: 1;
            }
          }
        `}</style>
        {/* Título y estadísticas compactas */}
        <div style={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          marginBottom: '20px',
          flexWrap: 'wrap',
          gap: '16px'
        }}>
          <h1 style={{ 
            color: theme.colors.primaryDark, 
            fontSize: '28px', 
            fontWeight: 600, 
            margin: 0
          }}>
            Tickets de Balanza
          </h1>

          <div style={{ 
            display: 'flex', 
            gap: '12px', 
            alignItems: 'center',
            flexWrap: 'wrap'
          }}>
            {/* Estadísticas compactas en la barra superior */}
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              padding: '4px 10px',
              backgroundColor: theme.colors.backgroundGray,
              borderRadius: '6px',
              border: `1px solid ${theme.colors.borderLight}`
            }}>
              <DatabaseOutlined style={{ fontSize: '14px', color: theme.colors.primary }} />
              <Text type="secondary" style={{ fontSize: '11px' }}>Total:</Text>
              <Text strong style={{ fontSize: '13px', color: theme.colors.textPrimary }}>
                {estadisticas.total}
              </Text>
            </div>

            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              padding: '4px 10px',
              backgroundColor: '#f6ffed',
              borderRadius: '6px',
              border: `1px solid #b7eb8f`
            }}>
              <CheckCircleOutlined style={{ fontSize: '14px', color: '#52c41a' }} />
              <Text type="secondary" style={{ fontSize: '11px' }}>Ingresos:</Text>
              <Text strong style={{ fontSize: '13px', color: '#52c41a' }}>
                {estadisticas.ingresos}
              </Text>
            </div>

            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              padding: '4px 10px',
              backgroundColor: '#fff7e6',
              borderRadius: '6px',
              border: `1px solid #ffe58f`
            }}>
              <WarningOutlined style={{ fontSize: '14px', color: '#faad14' }} />
              <Text type="secondary" style={{ fontSize: '11px' }}>Egresos:</Text>
              <Text strong style={{ fontSize: '13px', color: '#faad14' }}>
                {estadisticas.egresos}
              </Text>
            </div>

            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              padding: '4px 10px',
              backgroundColor: '#e6f7ff',
              borderRadius: '6px',
              border: `1px solid #91d5ff`
            }}>
              <DatabaseOutlined style={{ fontSize: '14px', color: '#1890ff' }} />
              <Text type="secondary" style={{ fontSize: '11px' }}>Kilos:</Text>
              <Text strong style={{ fontSize: '13px', color: '#1890ff' }}>
                {estadisticas.kilosTotal.toLocaleString('es-AR')}
              </Text>
            </div>

            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={handleNuevo}
              size="middle"
              style={{ 
                backgroundColor: theme.colors.primary, 
                borderColor: theme.colors.primary
              }}
            >
              Nuevo
            </Button>
          </div>
        </div>

      <StyledCard title="Filtros de Búsqueda">
        {/* Búsqueda global mejorada */}
        <div style={{ marginBottom: '20px' }}>
          <Input
            allowClear
            prefix={<SearchOutlined style={{ color: theme.colors.textSecondary }} />}
            placeholder="Buscar en todos los campos (ticket, cliente, chofer, producto, remito, DTV...)"
            value={busquedaRapida}
            onChange={(e) => setBusquedaRapida(e.target.value)}
            size="large"
            style={{ 
              width: '100%',
              maxWidth: '100%',
              fontSize: '14px'
            }}
          />
        </div>

        <Row gutter={[16, 20]} align="bottom">
            <Col xs={24} sm={12} md={5}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                <label style={{ 
                  fontWeight: 600, 
                  color: theme.colors.textPrimary,
                  fontSize: '13px',
                  whiteSpace: 'nowrap',
                  marginBottom: 0
                }}>
                  Producto:
                </label>
                <Select
                  placeholder={
                    filtros.estado === '1' 
                      ? (getProductosFiltrados().length > 0 ? "Solo productos tipo 2" : "No hay productos tipo 2")
                      : "Seleccionar producto"
                  }
                  style={{ width: '100%', fontSize: '13px' }}
                  size="middle"
                  value={filtros.producto}
                  onChange={(value) => {
                    setFiltros(prev => ({ ...prev, producto: value }));
                  }}
                  allowClear
                  loading={loadingProductos}
                  disabled={filtros.estado === '1' && getProductosFiltrados().length === 0}
                >
                  {getProductosFiltrados().map(producto => (
                    <Option key={producto.id} value={producto.id}>
                      {producto.descripcion}
                    </Option>
                  ))}
                </Select>
              </div>
            </Col>
            
            <Col xs={24} sm={12} md={5}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                <label style={{ 
                  fontWeight: 600, 
                  color: theme.colors.textPrimary,
                  fontSize: '13px',
                  whiteSpace: 'nowrap',
                  marginBottom: 0
                }}>
                  Estado:
                </label>
                <Select
                  placeholder="Seleccionar estado"
                  style={{ width: '100%', fontSize: '13px' }}
                  size="middle"
                  value={filtros.estado}
                  onChange={handleEstadoChange}
                  allowClear
                >
                  <Option value="1">1 - Pendiente Desmote</Option>
                  <Option value="2">2 - Pendiente 2da Pesada</Option>
                  <Option value="3">3 - Pendiente Recepcion/Despacho</Option>
                  <Option value="4">4 - Pendiente Modulo/Rollos</Option>
                </Select>
              </div>
            </Col>
            
            <Col xs={24} sm={12} md={7}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                <label style={{ 
                  fontWeight: 600, 
                  color: theme.colors.textPrimary,
                  fontSize: '13px',
                  whiteSpace: 'nowrap',
                  marginBottom: 0
                }}>
                  Rango de fechas:
                </label>
                <RangePicker
                  format="YYYY-MM-DD"
                  value={filtros.fechaRango}
                  onChange={(values) => setFiltros(prev => ({ ...prev, fechaRango: values as [Dayjs | null, Dayjs | null] }))}
                  style={{ width: '100%', fontSize: '13px' }}
                  size="middle"
                />
              </div>
            </Col>
            
            <Col xs={24} sm={24} md={7}>
              <div style={{ 
                display: 'flex', 
                alignItems: 'flex-end', 
                justifyContent: 'flex-end', 
                height: '100%', 
                paddingBottom: '4px', 
                gap: '12px',
                flexWrap: 'wrap'
              }}>
                <Button 
                  onClick={handleResetFiltros} 
                  size="middle"
                  style={{ 
                    fontSize: '13px',
                    minWidth: '100px',
                    height: '32px'
                  }}
                >
                  Limpiar
                </Button>
                <SearchButton
                  onClick={() => handleBuscar()}
                  loading={loading}
                  size="middle"
                  style={{
                    fontSize: '13px',
                    minWidth: '100px',
                    height: '32px'
                  }}
                />
              </div>
            </Col>
          </Row>
          {filtros.estado === '1' && (
            <Row style={{ marginTop: '8px' }}>
              <Col span={24}>
                <div style={{ fontSize: '12px', color: '#1890ff' }}>
                  {getProductosFiltrados().length > 0 
                    ? 'Mostrando solo productos tipo 2 (Pendiente desmote)'
                    : 'No hay productos tipo 2 disponibles para Pendiente Desmote'
                  }
                </div>
              </Col>
            </Row>
          )}
      </StyledCard>

      <StyledCard
        title="Pesajes"
        extra={
          <Space>
            <ColumnManagerDropdown
              manager={columnManager}
              labels={columnNames}
              requiredKeys={['acciones', 'numero_ticket']}
              buttonSize="small"
            />
            <ExportExcelButton
              onClick={handleExportExcel}
              size="small"
              disabled={getFilteredData.length === 0}
            />
            <ExportPDFButton
              onClick={handleExportPDF}
              size="small"
              disabled={getFilteredData.length === 0}
            />
          </Space>
        }
        bodyStyle={{ padding: '16px' }}
      >
        {/* Mensaje de resultados filtrados */}
        {busquedaRapida && (
          <div style={{ 
            marginBottom: '12px', 
            padding: '8px 12px', 
            backgroundColor: theme.colors.backgroundGray,
            borderRadius: theme.styles.borderRadius,
            fontSize: '14px',
            color: theme.colors.textSecondary
          }}>
            Mostrando {getFilteredData.length} de {pesajes.length} pesajes
            {busquedaRapida && ` que coinciden con "${busquedaRapida}"`}
          </div>
        )}
        <Table<Pesaje>
          dataSource={getFilteredData}
          columns={filteredColumns}
          loading={loading}
          rowKey="numero_ticket"
          scroll={{ x: 'max-content', y: 'max(150px, calc(100vh - 400px))' }}
          pagination={{
            current: currentPage,
            pageSize,
            total: serverStats.total,
            position: ['bottomRight'],
            showSizeChanger: true,
            showTotal: (total) => `${total} registros en total`,
            pageSizeOptions: ['50', '100', '200'],
            onChange: (page, newPageSize) => handleBuscar(page, newPageSize),
          }}
          size="middle"
          onRow={(record) => ({
            onMouseEnter: (e) => {
              (e.currentTarget as HTMLElement).style.backgroundColor = '#f8f9fa';
            },
            onMouseLeave: (e) => {
              (e.currentTarget as HTMLElement).style.backgroundColor = '';
            },
            style: {
              transition: 'background-color 0.2s ease'
            }
          })}
          onChange={(pagination, filters, sorter) => {
            setFilteredInfo(filters);
            if (!Array.isArray(sorter)) {
              const newSorterInfo = {
                columnKey: sorter.columnKey?.toString(),
                order: sorter.order,
              };
              setSorterInfo(newSorterInfo);
              // Guardar en localStorage
              localStorage.setItem('pesaje_filteredInfo', JSON.stringify(filters));
              localStorage.setItem('pesaje_sorterInfo', JSON.stringify(newSorterInfo));
            }
          }}
        />
      </StyledCard>
    </div>
    </App>
  );
};

export default ListadoPesajes; 